import * as Sentry from '@sentry/react';

/**
 * ENT-5492 client recovery contract for a protected-function 401.
 * `invokeProtectedFunction` derives it; callers and the unified session-expiry
 * handler only consume it.
 */
export type AuthRecoveryOutcome =
  | 'retried_with_stored_session'
  | 'refreshed_and_retried'
  | 'terminal'
  | 'terminal_duplicate_suppressed';

export interface ProtectedFunctionAuthRecovery {
  outcome: AuthRecoveryOutcome;
  functionName: string;
  /** Backend `errorCode` (or `code`) of the 401 that started recovery. */
  errorCode: string | null;
  requestId: string | null;
  /** Seconds since the rejected access token was issued (`iat`); null if undecodable. */
  tokenAgeSeconds: number | null;
  /** True when the rejected token was the stored session's current access token. */
  storedSessionMatched: boolean;
  refreshAttempted: boolean;
  /** Terminal only: auth library error code → HTTP status → "unknown". */
  terminalReason: string | null;
}

export const INVOLUNTARY_SIGN_OUT_EVENT = 'auth.involuntary_sign_out';

/**
 * D4: a stale access token recovered silently is not an incident — leave a
 * breadcrumb so a later event on the same page carries the history.
 */
export function recordStaleTokenRecovered(recovery: ProtectedFunctionAuthRecovery): void {
  Sentry.addBreadcrumb({
    category: 'auth',
    level: 'info',
    message: `stale access token recovered · ${recovery.functionName} · age ${recovery.tokenAgeSeconds ?? 'unknown'} s · storedSessionMatched=${recovery.storedSessionMatched}`,
    data: {
      outcome: recovery.outcome,
      errorCode: recovery.errorCode,
      requestId: recovery.requestId,
      refreshAttempted: recovery.refreshAttempted,
    },
  });
}

/**
 * D4: one readable warning per involuntary sign-out. No token material — only
 * the function, codes, ages and booleans of the recovery contract.
 */
export function captureInvoluntarySignOut(
  recovery: ProtectedFunctionAuthRecovery,
  route: string,
): void {
  const errorCode = recovery.errorCode ?? 'unknown';
  const terminalReason = recovery.terminalReason ?? 'unknown';

  Sentry.captureMessage(
    `Involuntary sign-out: ${recovery.functionName} → 401 ${errorCode}; refresh of stored session failed (${terminalReason})`,
    {
      level: 'warning',
      fingerprint: [INVOLUNTARY_SIGN_OUT_EVENT, recovery.functionName, terminalReason],
      tags: {
        function_name: recovery.functionName,
        error_code: errorCode,
        auth_recovery: 'terminal',
        release: Sentry.getClient()?.getOptions().release ?? 'unknown',
      },
      extra: {
        requestId: recovery.requestId,
        tokenAgeSeconds: recovery.tokenAgeSeconds,
        storedSessionMatched: recovery.storedSessionMatched,
        refreshAttempted: recovery.refreshAttempted,
        route,
      },
    },
  );
}
